'use client'

import {
  useCartMiniQuery,
  useCartQuery,
} from '@/hooks/react-query/cart/use-cart'
import { useIsBuy1Get1ValueMatchCampaignActive } from '@/lib/utils/campaign/campaign-active'
import { __cCollection2Service } from '@/services/client/__c.collection.service'
import type { TCollectionData } from '@/types/collection'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { getPreservedParamsForCollection } from '../constants'
import { useCollectionFilterStoreV2 } from '../store/collection-filter-store-v2'
import {
  areFiltersEqual,
  deriveEffectiveInputs,
  useInitialDataOnce,
} from '../utils/data-utils'
import { urlBuilder } from '../utils/url-builder.service'

interface UseCollectionDataProps {
  serverData: TCollectionData
  initialFilters?: Record<string, string | string[] | number | undefined>
}

// buildUrlAndExtractParams moved to data-utils

export const useCollectionData = ({
  serverData,
  initialFilters = {},
}: UseCollectionDataProps) => {
  const { filters, originalSlug, concatValue } = useCollectionFilterStoreV2(
    useShallow((state) => ({
      filters: state.filters,
      originalSlug: state.originalSlug,
      concatValue: state.concatValue,
    }))
  )

  const searchParams = useSearchParams()
  const mainProductId = searchParams.get('main_product_id')

  // Use initialData only once (first mount) to avoid reusing SSR data after interactions (e.g., clear filters)
  const usedInitialDataRef = useInitialDataOnce(false)

  // Track previous serverData slug to detect fresh server render (navigate back)
  const prevServerSlugRef = useRef<string | undefined>(undefined)

  // Reset usedInitialDataRef when receiving fresh serverData (navigate back)
  useEffect(() => {
    const currentSlug = serverData?.collection_detail?.seo_alias

    // Detect fresh server render: slug changed OR first mount
    if (currentSlug && currentSlug !== prevServerSlugRef.current) {
      // Fresh serverData detected → reset ref to allow using initialData
      usedInitialDataRef.current = false
      prevServerSlugRef.current = currentSlug
    }
  }, [serverData, usedInitialDataRef])

  const {
    effectiveOriginalSlug,
    effectiveFilters: baseEffectiveFilters,
    effectiveConcat,
    facets,
    cardFacets,
  } = deriveEffectiveInputs(
    serverData,
    originalSlug,
    filters,
    concatValue,
    initialFilters
  )

  // Fetch cart data only when needed (gian-hang-qua-tang + main_product_id exists)
  const shouldFetchCart =
    effectiveOriginalSlug === 'gian-hang-qua-tang' && !!mainProductId
  const { data: cartData } = useCartQuery()

  // Get mainProductPrice from cart when needed
  const mainProductPrice = useMemo(() => {
    if (!shouldFetchCart || !cartData?.items || !mainProductId) {
      return undefined
    }

    const mainProduct = cartData.items.find(
      (item) => item.index_id === mainProductId
    )

    return mainProduct?.regular_price
  }, [shouldFetchCart, cartData, mainProductId])

  // Check if this is buy-1-get-1-value-match collection
  const isBuy1Get1ValueMatchCollection =
    effectiveOriginalSlug === 'shop-all' ||
    effectiveOriginalSlug === 'buy-1-get-1-value-match'

  // Check if campaign is active
  const isCampaignActive = useIsBuy1Get1ValueMatchCampaignActive()

  // Fetch cart-mini when needed (buy-1-get-1-value-match + campaign active)
  const shouldFetchCartMini = isBuy1Get1ValueMatchCollection && isCampaignActive
  const { data: cartMiniData } = useCartMiniQuery({
    enabled: shouldFetchCartMini,
  })

  // Get max_eligible_price_for_gift from cart-mini when needed
  const maxEligiblePriceForGift = useMemo(() => {
    if (!shouldFetchCartMini || !cartMiniData) {
      return undefined
    }
    return cartMiniData.max_eligible_price_for_gift
  }, [shouldFetchCartMini, cartMiniData])

  // Add priority_regular_price to effectiveFilters if available
  const effectiveFilters = useMemo(() => {
    const filters = { ...baseEffectiveFilters }

    // Logic for gian-hang-qua-tang: use mainProductPrice
    if (mainProductPrice !== undefined) {
      filters.priority_regular_price = mainProductPrice
    }

    // Logic for buy-1-get-1-value-match: use max_eligible_price_for_gift
    // This takes precedence if both exist (shouldn't happen, but safer)
    if (maxEligiblePriceForGift !== undefined && maxEligiblePriceForGift > 0) {
      filters.priority_regular_price = maxEligiblePriceForGift
    }

    return filters
  }, [baseEffectiveFilters, mainProductPrice, maxEligiblePriceForGift])

  // Build URL và extract query params using UrlBuilderService
  const currentPathname =
    typeof window !== 'undefined' ? window.location.pathname : undefined
  const preservedParams = getPreservedParamsForCollection(effectiveOriginalSlug)
  const url = urlBuilder.buildCollectionUrl({
    originalSlug: effectiveOriginalSlug,
    filters: effectiveFilters,
    concatValue: effectiveConcat || null,
    currentPathname, // Pass currentPathname to preserve special rewritten URLs
    facets, // Pass facets để xác định concatFilterKey
    cardFacets, // Pass cardFacets để xác định concatFilterKey
    preserveParams: preservedParams.length > 0 ? preservedParams : undefined,
  })

  // Extract query params from URL
  const urlObj = new URL(
    url,
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  )
  const queryParams: Record<string, string | string[] | number | undefined> = {}
  urlObj.searchParams.forEach((value, key) => {
    queryParams[key] = value
  })

  const basePath = effectiveOriginalSlug.includes('/collection/')
    ? '/collection'
    : '/collection'

  // Simplified queryKey - chỉ cần những gì thực sự ảnh hưởng đến data
  // Include priority_regular_price in queryKey for proper caching
  const queryKey = [
    'collection-data',
    effectiveOriginalSlug,
    effectiveFilters,
    effectiveConcat,
    basePath,
    mainProductPrice, // Include mainProductPrice to ensure cache invalidation when price changes
    maxEligiblePriceForGift, // Include maxEligiblePriceForGift to ensure cache invalidation when value changes
  ]

  // Hydrate server data vào React Query
  // So sánh filters hiện tại với initialFilters (từ URL/serverData)
  const filtersMatchInitial = areFiltersEqual(effectiveFilters, initialFilters)
  // Chỉ dùng initialData khi:
  // 1. Có serverData
  // 2. Filters hiện tại match với initialFilters (= chưa có interaction)
  // 3. Chưa từng dùng initialData cho collection này
  const shouldUseInitialData =
    !!serverData && filtersMatchInitial && !usedInitialDataRef.current

  const initialData = shouldUseInitialData
    ? {
        pages: [
          {
            products: serverData.products || [],
            collection_detail: serverData.collection_detail,
            pagination: serverData.pagination || {
              total: 0,
              current_page: 1,
              has_next_page: false,
            },
            request: queryParams,
          },
        ],
        pageParams: [1],
      }
    : undefined

  // Mark initialData as used after first mount with this key
  useEffect(() => {
    if (shouldUseInitialData) {
      usedInitialDataRef.current = true
    }
    // Reset ref khi chuyển collection khác
    return () => {
      const isCollectionChanged =
        serverData?.collection_detail?.seo_alias !== effectiveOriginalSlug
      if (isCollectionChanged) {
        usedInitialDataRef.current = false
      }
    }
    // purposely not including usedInitialDataRef to avoid re-trigger
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUseInitialData, effectiveOriginalSlug, serverData])

  const query = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: async ({ pageParam = 1 }) => {
      if (!effectiveOriginalSlug) {
        throw new Error('Original slug not available')
      }

      // Create filters with page parameter
      const filtersWithPage = { ...effectiveFilters, page: pageParam }

      // Build URL và extract query params using UrlBuilderService
      const currentPathname =
        typeof window !== 'undefined' ? window.location.pathname : undefined
      const preservedParams = getPreservedParamsForCollection(
        effectiveOriginalSlug
      )
      const url = urlBuilder.buildCollectionUrl({
        originalSlug: effectiveOriginalSlug,
        filters: filtersWithPage,
        concatValue: effectiveConcat || null,
        currentPathname, // Pass currentPathname to preserve special rewritten URLs
        facets, // Pass facets để xác định concatFilterKey
        cardFacets, // Pass cardFacets để xác định concatFilterKey
        preserveParams:
          preservedParams.length > 0 ? preservedParams : undefined,
      })

      // Extract query params from URL
      const urlObj = new URL(
        url,
        typeof window !== 'undefined'
          ? window.location.origin
          : 'http://localhost'
      )
      const finalQueryParams: Record<
        string,
        string | string[] | number | undefined
      > = {}
      urlObj.searchParams.forEach((value, key) => {
        finalQueryParams[key] = value
      })

      // Build full slug from base slug + concat value (if exists)
      // This matches the pattern used by getBodyHtmlByCollectionPath API
      // Example: 'ao-nu' + 'xam' = 'ao-nu-xam'
      const fullSlug = effectiveConcat
        ? `${effectiveOriginalSlug}-${effectiveConcat}`
        : effectiveOriginalSlug

      // Add ignore_product_ids từ page 1 products (chỉ khi không phải page đầu tiên)
      if (pageParam > 1) {
        // Get current data from React Query cache
        const currentData = query.data
        if (currentData?.pages?.[0]?.products?.length > 0) {
          const page1ProductIds = currentData.pages[0].products
            .map((product: { id: string }) => product.id)
            .join(',')
          finalQueryParams.ignore_product_ids = page1ProductIds
        }
      }

      const data = await __cCollection2Service.getCollectionData(
        fullSlug,
        finalQueryParams
      )

      // Transform data to match expected type
      return {
        products: data.products || [],
        collection_detail: data.collection_detail,
        pagination: data.pagination || {
          total: 0,
          current_page: pageParam,
          has_next_page: false,
        },
        request: finalQueryParams,
      }
    },
    initialPageParam: 1,
    initialData,
    enabled: !!effectiveOriginalSlug, // Only run when originalSlug is available
    getNextPageParam: (lastPage) => {
      return lastPage.pagination?.has_next_page
        ? lastPage.pagination.current_page + 1
        : undefined
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
  })

  return query
}
